import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, Injectable, OnInit, signal } from '@angular/core';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core/index.js';
import dayGridPlugin from '@fullcalendar/daygrid';
import { EventClickArg } from '@fullcalendar/core';
import { NG_ModalService } from '../../bootstrap/modal/modalSvc.service';
import { UserService } from '../../user.service';
import { CommonModule } from '@angular/common';
import jaLocale from '@fullcalendar/core/locales/ja';


@Injectable({
	providedIn: 'root'
})
export class CommunityCalendarService {

	public events = signal<any[]>([]);
	language : number = 0; //default to English

	constructor(protected _usrSvc: UserService) {
		effect(() => {
			const tenant = this._usrSvc.currentTennant();
			if (tenant && tenant.Language > 0) {
				this.language = tenant.Language;
				console.log('CommunityCalendarService: Language resolved to', this.language);
				// Update events when language changes
				const selectedEvents = this.language === 2 ? this.events_japanese : this.events_english;
				console.log('CommunityCalendarService: Setting events to', this.language === 2 ? 'Japanese' : 'English', selectedEvents);
				this.events.set(selectedEvents);
			}
		});
	}

	getRandomDateInCurrentMonth(): string {
		const now = new Date();
		const year = now.getFullYear();
		const month = now.getMonth();
		const day = Math.floor(Math.random() * 28) + 1; // To avoid issues with February
		const randomDate = new Date(year, month, day);
		return randomDate.toISOString().split('T')[0]; // Return in 'YYYY-MM-DD' format
	}

	getFirstOfTheMonth(): string {
		const now = new Date();
		const year = now.getFullYear();
		const month = now.getMonth();
		const firstDate = new Date(year, month, 1);
		return firstDate.toISOString().split('T')[0]; // Return in 'YYYY-MM-DD' format
	}

	paymentDueColor = '#e74c3c'; // Red
	paymentReceivedColor = '#27ae60'; // Green

	events_english: any[] = [
		{ title: "Rental Due", color: this.paymentDueColor, date: this.getFirstOfTheMonth(), data: { 'description': 'Your monthly rent is due today. Please ensure timely payment to avoid any late fees.' } },
		{ title: "Mochi Making Gathering", date: this.getRandomDateInCurrentMonth(), data: { 'description': "Join us for a traditional mochi making event! Learn to make fresh mochi and enjoy tea with your neighbors." } },
		{ title: 'Community Clean-up Day', date: this.getRandomDateInCurrentMonth(), data: { 'description': 'Help beautify our neighborhood! Volunteers are welcome to join in cleaning and maintaining common areas.' } },
		{ title: 'Payment Scheduled', color: this.paymentReceivedColor , date: this.getRandomDateInCurrentMonth(), data: { 'description': 'Your rent payment has been scheduled for this date. No further action is required.' } },
		{ title: 'Seasonal Tea Ceremony', date: this.getRandomDateInCurrentMonth(), data: { 'description': 'Experience a traditional Japanese tea ceremony. All residents are invited to participate and learn about this cultural practice.' } },
	];

	events_japanese: any[] = [
		{ title: "家賃支払い期限", color: this.paymentDueColor, date: this.getFirstOfTheMonth(), data: { 'description': '本日が月々の家賃支払い期限です。遅延料金を避けるため、期限内にお支払いください。' } },
		{ title: "餅つき大会", date: this.getRandomDateInCurrentMonth(), data: { 'description': "伝統的な餅つきイベントにご参加ください！新鮮な餅を作り、ご近所の方々とお茶を楽しみましょう。" } },
		{ title: '地域清掃の日', date: this.getRandomDateInCurrentMonth(), data: { 'description': '地域を美しくするお手伝いをしましょう！共用エリアの清掃と維持にボランティアとして参加してください。' } },
		{ title: '支払い予定日', color: this.paymentReceivedColor , date: this.getRandomDateInCurrentMonth(), data: { 'description': 'この日に家賃の支払いが予定されています。これ以上の手続きは不要です。' } },
		{ title: '季節のお茶会', date: this.getRandomDateInCurrentMonth(), data: { 'description': '伝統的な日本の茶道を体験してください。すべての居住者が参加し、この文化的な習慣について学ぶことができます。' } },
	];


	getEvents() : any[]  {
		if (this.language === 2) {
			return this.events_japanese;
		} else {
			return this.events_english;
		}
	}

	addEvent(event: any) {
		this.events.update(events => [...events, event]);
	}

	removeEvent(eventTitle: string) {
		this.events.update(events => events.filter(event => event.title !== eventTitle));
	}

	updateEvent(eventTitle: string, updatedEvent: any) {
		const index = this.events().findIndex(event => event.title === eventTitle);
		if (index !== -1) {
			const updatedEvents = [...this.events()];
			updatedEvents[index] = { ...updatedEvents[index], ...updatedEvent };
			this.events.set(updatedEvents);
		}
	}	

}



@Component({
	selector: 'app-community-calendar',
	imports: [FullCalendarModule, CommonModule],
	templateUrl: './community-calendar.html',
	styleUrl: './community-calendar.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityCalendar {

	calendarOptions!: CalendarOptions;
	language : number = 0;
	calendarInitialized : boolean = false;
	
	constructor( protected modalSvc: NG_ModalService, protected calendarService: CommunityCalendarService, protected userService: UserService, protected _cd: ChangeDetectorRef ) { 
		effect(() => {
			let tenant = this.userService.currentTennant();
			let events = this.calendarService.events();
			
			console.log('CommunityCalendar: Tenant Language:', tenant?.Language);
			console.log('CommunityCalendar: Events count:', events.length);
			console.log('CommunityCalendar: First event:', events[0]);
			
			if (tenant.Language > 0  && events.length > 0) {
				console.log('CommunityCalendar: Initializing calendar with language', tenant.Language);
				this.language = tenant.Language;
				// Re-initialize calendar when language changes and events are ready
				this.initCalendarOptions(tenant.Language);
				this.calendarInitialized = true;
				this._cd.detectChanges();
			}
		});
	}

	initCalendarOptions(language: number) {
		let events : any[] = [];
		if (language == 2) {
			events = this.calendarService.events_japanese;
		} else {
			events = this.calendarService.events_english;
		}
		this.calendarOptions = {
			plugins: [dayGridPlugin],
			initialView: 'dayGridMonth',
			locale: language == 2 ? jaLocale : 'en',
			events: events, // Use the selected events array
			height: 'auto',
			contentHeight: 'auto',
			aspectRatio: 1,
			expandRows: true,
			handleWindowResize: true,
			windowResizeDelay: 100,
			stickyHeaderDates: true,
			stickyFooterScrollbar: true,
			eventClick: this.handleEventClick.bind(this)
		};
	}
	handleEventClick(clickInfo: EventClickArg) {
		console.log('Event clicked:', clickInfo);
		let modalContent = {
			title: clickInfo.event.title,
			bodyStrings: [],
			bodyHtml: `
			<div style="width:100%; text-align:center">
				<h3>` + (clickInfo.event.extendedProps as any)?.['data']?.['description'] + `</h3>
				<div> Date: ` + clickInfo.event.startStr + `</div>
			</div>`,
			buttons: [],
		};
		this.modalSvc.setContent(modalContent);
		this.modalSvc.showModal();
		this._cd.detectChanges();
	}
}

//create a service here to manage the calendar events

//you can add, remove, update events in the service

//then inject the service into this component to get the events

